import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, User } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useAuthStore } from '@/store/auth.store'
import { inboxTasksService } from '@/services/inboxTasksService'
import { approvalRouteDetailService } from '@/services/approvalRouteDetailService'
import { employeesService } from '@/services/employeesService'
import { loanRequestService } from '@/services/loanRequestService'
import { leaveRequestService } from '@/services/leaveRequestService'
import { expenseClaimService } from '@/services/expenseClaimService'
import type {
  InboxTaskListItem,
  ApprovalStatusItem,
  LoanLookupItem,
  LoanRequestVm,
  LeaveRequestVm,
  LeaveRequestLookupItem,
  ExpenseClaimVm,
} from '@/api/types'
import type { Employee } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'

const EMPLOYEE_APPROVAL_RECORD_NAME = 'Employee Approval Request'
const LOAN_APPROVAL_RECORD_NAME = 'Loan Request'
const LEAVE_APPROVAL_RECORD_NAME = 'Leave Request'
const EXPENSE_CLAIM_APPROVAL_RECORD_NAME = 'Expense Claim Request'
const normalizeRecordName = (s?: string | null) => (s ?? '').toLowerCase().replace(/\s+/g, '').trim()
const isEmployeeApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(EMPLOYEE_APPROVAL_RECORD_NAME)
const isLoanApprovalRecordName = (s?: string | null) => normalizeRecordName(s) === normalizeRecordName(LOAN_APPROVAL_RECORD_NAME)
const isLeaveApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(LEAVE_APPROVAL_RECORD_NAME)
const isExpenseClaimApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(EXPENSE_CLAIM_APPROVAL_RECORD_NAME)

/**
 * Approval Detail – opens when Task Inbox action button is clicked.
 * Loads record by id (GetById), then:
 * - Top left: Employee/Request detail (if recordName is Employee Approval Request, load employee by recordId)
 * - Bottom left: Approvers (GetApprovalUsers by approvalTaskId)
 * - Right: Requested By (GetUserById requestedBy → empId → employee name), Record Name, Department, Approval Status, Approval Notes, Submit (ApproveRequest)
 */
export const ApprovalDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [data, setData] = useState<InboxTaskListItem | null>(null)
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [loanRequest, setLoanRequest] = useState<LoanRequestVm | null>(null)
  const [leaveRequest, setLeaveRequest] = useState<LeaveRequestVm | null>(null)
  const [expenseClaim, setExpenseClaim] = useState<ExpenseClaimVm | null>(null)
  const [approvers, setApprovers] = useState<{ id: number; userName?: string }[]>([])
  const [requestedByName, setRequestedByName] = useState<string>('')
  const [departmentName, setDepartmentName] = useState<string>('')
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [approvalStatusId, setApprovalStatusId] = useState<number>(0)
  const [approvalNote, setApprovalNote] = useState<string>('')
  const [loanTypes, setLoanTypes] = useState<LoanLookupItem[]>([])
  const [leaveTypes, setLeaveTypes] = useState<LeaveRequestLookupItem[]>([])
  const [leaveSubstituteName, setLeaveSubstituteName] = useState<string>('—')

  const recordId = data?.recordId ?? 0
  const approvalTaskId = data?.approvalTaskId ?? 0
  const requestedBy = data?.requestedBy ?? 0

  // Load main record by id
  useEffect(() => {
    if (!id || !branchId) return
    const numId = parseInt(id, 10)
    if (!numId) return

    const load = async () => {
      setLoading(true)
      try {
        const record = await inboxTasksService.getById(numId)
        setData(record)
        if (record) {
          setApprovalStatusId(record.approvalStatusId ?? 0)
          setApprovalNote(record.approvalNote ?? '')
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load approval detail')
        setData(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, branchId])

  // Load departments and statuses for dropdowns
  useEffect(() => {
    const load = async () => {
      try {
        const [depts, st] = await Promise.all([
          employeesService.getDepartmentLookups(),
          inboxTasksService.getApprovalStatuses(),
        ])
        setDepartments(depts ?? [])
        setStatuses(st ?? [])
      } catch {
        setDepartments([])
        setStatuses([])
      }
    }
    load()
  }, [])

  // When recordName is Employee Approval Request, load employee by recordId (top-left section)
  useEffect(() => {
    if (!data || !isEmployeeApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setEmployee(null)
      return
    }
    const load = async () => {
      try {
        const emp = await employeesService.getEmployeeById(branchId, recordId)
        setEmployee(emp)
      } catch {
        setEmployee(null)
      }
    }
    load()
  }, [data, recordId, branchId])

  // When recordName is Loan Request, load loan by recordId and then load related employee via loan.empId
  useEffect(() => {
    if (!data || !isLoanApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setLoanRequest(null)
      setEmployee(null)
      return
    }

    const load = async () => {
      try {
        const loan = await loanRequestService.getById(recordId, branchId)
        setLoanRequest(loan)

        const empId = loan?.empId ?? 0
        if (!empId) {
          setEmployee(null)
          return
        }
        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)
      } catch {
        setLoanRequest(null)
        setEmployee(null)
      }
    }

    load()
  }, [data, recordId, branchId])

  // Load loan types only for Loan Request approval pages
  useEffect(() => {
    if (!data || !isLoanApprovalRecordName(data.recordName)) {
      setLoanTypes([])
      return
    }

    const load = async () => {
      try {
        const list = await loanRequestService.getLoanTypes()
        setLoanTypes(list || [])
      } catch {
        setLoanTypes([])
      }
    }

    load()
  }, [data])

  // When recordName is Leave Request, load leave by recordId and then load related employee via leave.empId
  useEffect(() => {
    if (!data || !isLeaveApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setLeaveRequest(null)
      setEmployee(null)
      return
    }

    const load = async () => {
      try {
        const leave = await leaveRequestService.getById(recordId, branchId)
        setLeaveRequest(leave)

        const empId = leave?.empId ?? 0
        if (!empId) {
          setEmployee(null)
          return
        }

        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)
      } catch {
        setLeaveRequest(null)
        setEmployee(null)
      }
    }

    load()
  }, [data, recordId, branchId])

  // When recordName is Expense Claim Request, load expense claim by recordId, then load related employee via claim.empId/employeeId
  useEffect(() => {
    if (!data || !isExpenseClaimApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setExpenseClaim(null)
      return
    }

    const load = async () => {
      try {
        const claim = await expenseClaimService.getById(recordId, branchId)
        setExpenseClaim(claim)

        const empId = claim?.employeeId ?? 0
        if (!empId) {
          setEmployee(null)
          return
        }
        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)
      } catch {
        setExpenseClaim(null)
        setEmployee(null)
      }
    }

    load()
  }, [data, recordId, branchId])

  // Load leave types only for Leave Request approval pages
  useEffect(() => {
    if (!data || !isLeaveApprovalRecordName(data.recordName)) {
      setLeaveTypes([])
      return
    }

    const load = async () => {
      try {
        const list = await leaveRequestService.getLeaveTypes()
        setLeaveTypes(list || [])
      } catch {
        setLeaveTypes([])
      }
    }

    load()
  }, [data])

  // Load substitute employee name (optional)
  useEffect(() => {
    const subId = leaveRequest?.substituteEmpId ?? 0
    if (!data || !isLeaveApprovalRecordName(data.recordName) || !branchId) return

    if (!subId) {
      setLeaveSubstituteName('—')
      return
    }

    const load = async () => {
      try {
        const emp = await employeesService.getEmployeeById(branchId, subId)
        const name = [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ') || emp.empFirstNameAr || ''
        setLeaveSubstituteName(name || '—')
      } catch {
        setLeaveSubstituteName('—')
      }
    }

    load()
  }, [data, branchId, leaveRequest])

  // Approvers list: GetApprovalUsers(branchId, approvalTaskId)
  useEffect(() => {
    if (!branchId || !approvalTaskId) {
      setApprovers([])
      return
    }
    const load = async () => {
      try {
        const list = await approvalRouteDetailService.getApprovalUsers(branchId, approvalTaskId)
        setApprovers(list)
      } catch {
        setApprovers([])
      }
    }
    load()
  }, [branchId, approvalTaskId])

  // Requested By: GetUserById(branchId, requestedBy) → empId → get employee name
  useEffect(() => {
    if (!branchId || !requestedBy) {
      setRequestedByName('')
      return
    }
    const load = async () => {
      try {
        const u = await approvalRouteDetailService.getUserById(branchId, requestedBy)
        if (!u?.empId) {
          setRequestedByName(u?.userName ?? '')
          return
        }
        const emp = await employeesService.getEmployeeById(branchId, u.empId)
        const name = [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ') || emp.empFirstNameAr || ''
        setRequestedByName(name || u.userName || '')
      } catch {
        setRequestedByName('')
      }
    }
    load()
  }, [branchId, requestedBy])

  // Department name: from employee (recordId) using departments lookup
  useEffect(() => {
    if (!employee) {
      setDepartmentName('')
      return
    }
    const deptId = employee.empDepartmetId ?? 0
    if (!deptId) {
      setDepartmentName(employee.departmentName ?? '')
      return
    }
    const found = departments.find((d) => d.id === deptId)
    setDepartmentName(found ? (found.lookupNameEn || found.lookupNameAr || '') : (employee.departmentName ?? ''))
  }, [employee, departments])

  const statusOptions = useMemo(() => statuses.filter((s) => s.id), [statuses])

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  const formatMoney = (n?: number | null) => {
    const v = Number(n ?? 0)
    if (!Number.isFinite(v)) return '—'
    return v.toFixed(4)
  }

  const handleSubmit = async () => {
    if (!data?.id) return
    const note = approvalNote.trim()
    if (!approvalStatusId) {
      toast.error('Please select Approval Status.')
      return
    }
    setSubmitting(true)
    const payload = { id: data.id, approvalNote: note, approvalStatusId }
    console.log('Approval Detail – Update payload:', JSON.stringify(payload, null, 2))
    try {
      await approvalRouteDetailService.approveRequest(data.id, note, approvalStatusId)
      await showSuccess('Submitted', 'Approval updated successfully.')
      navigate('/approval-jobs')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const loanTypeLabel = (lt: LoanLookupItem) =>
    lt.lookupNameEn || lt.lookupNameAr || lt.nameEn || lt.nameAr || lt.name || String(lt.codeType ?? lt.id)

  const employeeFullName = (emp: Employee | null) => {
    if (!emp) return ''
    return [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ') || emp.empFirstNameAr || ''
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('navigation.workflow')} description="Approval Detail" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: two sections */}
        <div className="lg:col-span-2 space-y-6">
          {/* Top left: Employee / Request detail */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">
                {isLoanApprovalRecordName(data.recordName)
                  ? 'LOAN REQUEST'
                  : isLeaveApprovalRecordName(data.recordName)
                    ? 'LEAVE REQUEST'
                    : isExpenseClaimApprovalRecordName(data.recordName)
                      ? 'EXPENSE CLAIM REQUEST'
                    : 'ADD EMPLOYEE DETAIL / REQUEST DETAIL'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {isEmployeeApprovalRecordName(data.recordName) && employee ? (
                <div className="flex gap-4 flex-wrap">
                  <Avatar className="h-20 w-20 rounded-full">
                    <AvatarFallback>
                      <User className="h-10 w-10" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-muted-foreground">First Name:</span> {employee.empFirstNameEn ?? '—'}</div>
                    <div><span className="text-muted-foreground">Second Name:</span> {employee.empSecondNameEn ?? '—'}</div>
                    <div><span className="text-muted-foreground">Family Name:</span> {employee.empLastNameEn ?? '—'}</div>
                    <div><span className="text-muted-foreground">Email:</span> {employee.empEmail ?? '—'}</div>
                    <div><span className="text-muted-foreground">DOB:</span> {formatDate(employee.empDob)}</div>
                    <div className="col-span-2"><span className="text-muted-foreground">Department:</span> {employee.departmentName ?? (departmentName || '—')}</div>
                    <div><span className="text-muted-foreground">Nationality ID:</span> {employee.empNationalityId ?? '—'}</div>
                    <div><span className="text-muted-foreground">Address:</span> {employee.empAddress ?? '—'}</div>
                    <div><span className="text-muted-foreground">Reference:</span> {employee.empRefrence ?? employee.empReferenceContact ?? '—'}</div>
                  </div>
                </div>
              ) : isLoanApprovalRecordName(data.recordName) && loanRequest && employee ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">Employee Name</div>
                  <div>{employeeFullName(employee) || '—'}</div>

                  <div className="text-muted-foreground">Loan Type</div>
                  <div>
                    {(() => {
                      const selected = loanTypes.find((x) => x.id === (loanRequest.loanTypeId ?? 0))
                      return selected ? loanTypeLabel(selected) : '—'
                    })()}
                  </div>

                  <div className="text-muted-foreground">Start Date</div>
                  <div>{formatDate(loanRequest.startDate)}</div>

                  <div className="text-muted-foreground">Loan Amount</div>
                  <div>{loanRequest.loanAmount ?? '—'}</div>

                  <div className="text-muted-foreground">Deduction Amount</div>
                  <div>{loanRequest.deductAmount ?? '—'}</div>

                  <div className="text-muted-foreground">Details</div>
                  <div className="break-words">{loanRequest.details ?? '—'}</div>
                </div>
              ) : isLeaveApprovalRecordName(data.recordName) && leaveRequest && employee ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">Employee Name</div>
                  <div>{employeeFullName(employee) || '—'}</div>

                  <div className="text-muted-foreground">Leave Type</div>
                  <div>
                    {(() => {
                      const selected = leaveTypes.find((x) => x.id === (leaveRequest.leaveTypeId ?? 0))
                      if (!selected) return '—'
                      return selected.name ?? selected.nameEn ?? selected.lookupNameEn ?? '—'
                    })()}
                  </div>

                  <div className="text-muted-foreground">From</div>
                  <div>{formatDate(leaveRequest.fromDate || null)}</div>

                  <div className="text-muted-foreground">To</div>
                  <div>{formatDate(leaveRequest.toDate || null)}</div>

                  <div className="text-muted-foreground">Substitute Id</div>
                  <div>{leaveRequest.substituteEmpId ? leaveSubstituteName : '—'}</div>

                  <div className="text-muted-foreground">Reason</div>
                  <div className="break-words">{leaveRequest.details ?? '—'}</div>
                </div>
              ) : isExpenseClaimApprovalRecordName(data.recordName) && expenseClaim ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">Employee Name</div>
                  <div>{employeeFullName(employee) || '—'}</div>

                  <div className="text-muted-foreground">Claim Date</div>
                  <div>{formatDate(expenseClaim.claimDate || expenseClaim.expenseDate || null)}</div>

                  <div className="text-muted-foreground">Details</div>
                  <div className="break-words">{expenseClaim.details || '—'}</div>

                  <div className="text-muted-foreground">Amount</div>
                  <div>{formatMoney(expenseClaim.amount ?? 0)}</div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No employee detail for this request type.</p>
              )}
            </CardContent>
          </Card>

          {/* Bottom left: Approvers */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">APPROVERS</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-3">
                {approvers.map((a) => (
                  <li key={a.id} className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-xs">
                        {(a.userName || '').slice(0, 2).toUpperCase() || '—'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{a.userName ?? '—'}</span>
                  </li>
                ))}
                {approvers.length === 0 && (
                  <li className="text-sm text-muted-foreground">No approvers loaded.</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Right column: Approval action form */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle className="text-base">Approval</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <Label>Requested By *</Label>
              <Input readOnly value={requestedByName} className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>Record Name *</Label>
              <Input readOnly value={data.recordName ?? ''} className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>Department *</Label>
              <Input readOnly value={departmentName} className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>Approval Status *</Label>
              <Select
                value={approvalStatusId ? String(approvalStatusId) : ''}
                onValueChange={(v) => setApprovalStatusId(Number(v) || 0)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.lookupNameEn || s.lookupNameAr || 'Unnamed'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Approval Notes</Label>
              <Textarea
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                placeholder="Notes"
                rows={4}
              />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : 'Submit'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
